import { useCallback, useEffect, useMemo, useRef } from "react";

import { useSelector } from "react-redux";

import { IRootState } from "config/redux";
import { ServiceConstants } from "constant";
import { BankSelectionService } from "services";
import { Bank, BankCreditNoteResponse, BankCreditResponse, NoteType } from "types";

import {
  buildAutomaticBankCreditNotes,
  managedAutomaticBankCreditNoteCodes,
} from "./rules";

const { REQUEST_PENDING, REQUEST_SUCCESS } = ServiceConstants;

const automaticNoteKey = (note: Pick<BankCreditNoteResponse, "bank" | "code">) =>
  `${note.bank}:${note.code}`;

type AffectedNoteList = Array<{ bank: Bank; noteType: NoteType }>;

const affectedNoteKey = (note: { bank: Bank; noteType: NoteType }) =>
  `${note.bank}:${note.noteType}`;

const uniqueAffectedNotes = (notes: AffectedNoteList) =>
  Array.from(new Map(notes.map((note) => [affectedNoteKey(note), note])).values());

const summarizeNote = (note: {
  id?: number;
  bank: Bank;
  code?: string;
  note: string;
  noteType: NoteType;
}) => ({
  id: note.id,
  bank: note.bank,
  code: note.code,
  note: note.note,
  noteType: note.noteType,
});

export function useAutomaticBankCreditNotes(bankCredit?: BankCreditResponse) {
  const syncSignatureRef = useRef<string>();
  const refetchTimeoutRef = useRef<number>();
  const pendingRefetchNotesRef = useRef<AffectedNoteList>([]);
  const bankCreditRequestId = bankCredit?.id;

  const allNotesRequestId = useMemo(
    () =>
      bankCreditRequestId
        ? BankSelectionService.bankNotesRequestId(bankCreditRequestId)
        : undefined,
    [bankCreditRequestId],
  );

  const allNotesResponse = useSelector((state: IRootState) =>
    allNotesRequestId ? state.service.object[allNotesRequestId] : undefined,
  );

  const refetchNotes = useCallback((requestId: number, affectedNotes: AffectedNoteList) => {
    BankSelectionService.bankNotesAll(requestId);
    uniqueAffectedNotes(affectedNotes).forEach((note) => {
      BankSelectionService.bankNotesAll(requestId, note.bank, note.noteType);
    });
  }, []);

  const scheduleRefetchNotes = useCallback(
    (requestId: number, affectedNotes: AffectedNoteList) => {
      pendingRefetchNotesRef.current = uniqueAffectedNotes([
        ...pendingRefetchNotesRef.current,
        ...affectedNotes,
      ]);

      if (refetchTimeoutRef.current) {
        window.clearTimeout(refetchTimeoutRef.current);
      }

      refetchTimeoutRef.current = window.setTimeout(() => {
        const notesToRefetch = pendingRefetchNotesRef.current;
        pendingRefetchNotesRef.current = [];
        refetchNotes(requestId, notesToRefetch);
      }, 700);
    },
    [refetchNotes],
  );

  useEffect(() => {
    if (!bankCreditRequestId) {
      return;
    }
    BankSelectionService.bankNotesAll(bankCreditRequestId);
  }, [bankCreditRequestId]);

  useEffect(
    () => () => {
      if (refetchTimeoutRef.current) {
        window.clearTimeout(refetchTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!bankCredit || !bankCreditRequestId || !allNotesResponse) {
      return;
    }

    if (allNotesResponse.status === REQUEST_PENDING) {
      return;
    }

    if (allNotesResponse.status !== REQUEST_SUCCESS || !Array.isArray(allNotesResponse.data)) {
      return;
    }

    const existingNotes = allNotesResponse.data as BankCreditNoteResponse[];
    const existingAutomaticNotes = existingNotes.filter(
      (note) =>
        note.creationType === "AUTOMATIC" &&
        note.code &&
        managedAutomaticBankCreditNoteCodes.has(note.code) &&
        note.info?.active !== false,
    );
    const existingAutomaticNotesByKey = new Map(
      existingAutomaticNotes.map((note) => [automaticNoteKey(note), note]),
    );
    const desiredAutomaticNotes = buildAutomaticBankCreditNotes(bankCredit);
    const desiredAutomaticNotesByKey = new Map(
      desiredAutomaticNotes.map((note) => [automaticNoteKey(note), note]),
    );

    const signature = JSON.stringify({
      bankCreditRequestId,
      desired: desiredAutomaticNotes.map(summarizeNote),
      existing: existingAutomaticNotes.map(summarizeNote),
    });

    if (syncSignatureRef.current === signature) {
      return;
    }

    syncSignatureRef.current = signature;

    const affectedNotes: AffectedNoteList = [];

    desiredAutomaticNotes.forEach((desiredNote) => {
      const existingNote = existingAutomaticNotesByKey.get(automaticNoteKey(desiredNote));
      if (
        !existingNote ||
        existingNote.note !== desiredNote.note ||
        existingNote.noteType !== desiredNote.noteType
      ) {
        affectedNotes.push({ bank: desiredNote.bank, noteType: desiredNote.noteType });
        BankSelectionService.bankNoteCreate(desiredNote);
      }
    });

    existingAutomaticNotes.forEach((existingNote) => {
      if (!desiredAutomaticNotesByKey.has(automaticNoteKey(existingNote))) {
        affectedNotes.push({ bank: existingNote.bank, noteType: existingNote.noteType });
        BankSelectionService.bankNoteDelete(existingNote.id);
      }
    });

    if (affectedNotes.length > 0) {
      scheduleRefetchNotes(bankCreditRequestId, affectedNotes);
    }
  }, [allNotesResponse, bankCredit, bankCreditRequestId, scheduleRefetchNotes]);
}
